import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo, useCallback } from 'react';
import { db } from '../config/firebase';
import { doc, setDoc, onSnapshot } from 'firebase/firestore';
import { useAuth } from './AuthContext';
import { useOrders } from './OrderContext';
import { logger } from '../utils/logger';
import { COLLECTIONS } from '../config/constants';

// Zaman dilimi kontenjan ayarı tipi
export interface TimeSlotQuotaSettings {
  defaultQuota: number; // Varsayılan kontenjan (örn: 10)
  customQuotas?: { [slot: string]: number }; // Özel slot kotaları (opsiyonel)
}

// Belirli bir tarih + slot için kullanım
export interface SlotUsage {
  date: string; // YYYY-MM-DD
  slot: string; // 10:00-12:00
  used: number; // Kaç sipariş var
  quota: number; // Maksimum kontenjan
  available: number; // Kalan
}

interface TimeSlotContextType {
  // Ayarlar
  quotaSettings: TimeSlotQuotaSettings;
  updateQuotaSettings: (settings: TimeSlotQuotaSettings) => Promise<void>;
  
  // Kullanım sorguları
  getSlotUsage: (date: string, slot: string) => SlotUsage;
  isSlotAvailable: (date: string, slot: string) => boolean;
  getAllSlotsForDate: (date: string) => SlotUsage[];
  
  // Realtime data
  loading: boolean;
}

const TimeSlotContext = createContext<TimeSlotContextType | undefined>(undefined);

export const useTimeSlots = () => {
  const context = useContext(TimeSlotContext);
  if (!context) {
    throw new Error('useTimeSlots must be used within TimeSlotProvider');
  }
  return context;
};

interface TimeSlotProviderProps {
  children: ReactNode;
}

const DEFAULT_SETTINGS: TimeSlotQuotaSettings = {
  defaultQuota: 10,
  customQuotas: {},
};

export const TimeSlotProvider: React.FC<TimeSlotProviderProps> = ({ children }) => {
  const [quotaSettings, setQuotaSettings] = useState<TimeSlotQuotaSettings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const { currentUser } = useAuth();
  const isAdmin = (currentUser?.role || '').toLowerCase() === 'admin';
  const { orders } = useOrders();

  // Firestore'dan ayarları yükle
  useEffect(() => {
    if (!currentUser || !isAdmin) {
      setQuotaSettings(DEFAULT_SETTINGS);
      setLoading(false);
      return;
    }

    const settingsRef = doc(db, COLLECTIONS.TIME_SLOT_SETTINGS, 'global');
    
    const unsubscribe = onSnapshot(
      settingsRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data() as TimeSlotQuotaSettings;
          setQuotaSettings({
            defaultQuota: data.defaultQuota || 10,
            customQuotas: data.customQuotas || {},
          });
        } else {
          setQuotaSettings(DEFAULT_SETTINGS);
        }
        setLoading(false);
      },
      (error) => {
        logger.error('TimeSlot ayarları yüklenirken hata:', error);
        setQuotaSettings(DEFAULT_SETTINGS);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [currentUser, isAdmin]);

  // Ayarları güncelle
  const updateQuotaSettings = useCallback(async (settings: TimeSlotQuotaSettings) => {
    if (!isAdmin) {
      const error = new Error('Sadece yöneticiler zaman dilimi ayarlarını güncelleyebilir.');
      logger.warn('Yetkisiz time slot güncelleme denemesi yapıldı.');
      throw error;
    }

    try {
      const settingsRef = doc(db, COLLECTIONS.TIME_SLOT_SETTINGS, 'global');
      await setDoc(settingsRef, settings, { merge: true });
      logger.info('TimeSlot ayarları güncellendi:', settings);
    } catch (error) {
      logger.error('TimeSlot ayarları güncellenirken hata:', error);
      throw error;
    }
  }, [isAdmin]);

  // Belirli tarih + slot için kullanım hesapla
  const getSlotUsage = useCallback((date: string, slot: string): SlotUsage => {
    // O tarih + slot için kaç sipariş var
    const used = orders.filter(
      (order) => 
        order.deliveryDate === date && 
        order.deliverySlot === slot &&
        order.deliveryStatus !== 'cancelled' // İptal edilenler sayılmaz
    ).length;

    // Kontenjan: önce özel bakılır, yoksa varsayılan
    const quota = quotaSettings.customQuotas?.[slot] ?? quotaSettings.defaultQuota;
    
    const available = Math.max(0, quota - used);

    return {
      date,
      slot,
      used,
      quota,
      available,
    };
  }, [orders, quotaSettings]);

  // Slot müsait mi?
  const isSlotAvailable = useCallback((date: string, slot: string): boolean => {
    const usage = getSlotUsage(date, slot);
    return usage.available > 0;
  }, [getSlotUsage]);

  // Bir tarih için tüm slotların durumu
  const getAllSlotsForDate = useCallback((date: string): SlotUsage[] => {
    const SLOTS = [
      '10:00-12:00',
      '12:00-14:00',
      '14:00-16:00',
      '16:00-18:00',
    ];

    return SLOTS.map((slot) => getSlotUsage(date, slot));
  }, [getSlotUsage]);

  // Context value memoization - gereksiz re-render'ları önler
  const value = useMemo<TimeSlotContextType>(() => ({
    quotaSettings,
    updateQuotaSettings,
    getSlotUsage,
    isSlotAvailable,
    getAllSlotsForDate,
    loading,
  }), [quotaSettings, updateQuotaSettings, getSlotUsage, isSlotAvailable, getAllSlotsForDate, loading]);

  return (
    <TimeSlotContext.Provider value={value}>
      {children}
    </TimeSlotContext.Provider>
  );
};

export default TimeSlotContext;
